import {CloseOutlined, DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Card, Col, Row} from 'antd';
import {
  AccountExtraFeeInput,
  AccountExtraFeeRegion,
  AccountExtraFeeType,
  BillingPlans,
  PaymentMethods,
  UpdateMasterAccountInput
} from 'api/generated';
import {AnchorLink} from 'components';
import {UrlInput} from 'components/Form/UrlInput';
import {FieldArray, useFormikContext} from 'formik';
import {AutoComplete, FormItem, Input, InputNumber, Select} from 'formik-antd';
import {useBusinessCategories} from 'hooks/useBusinessCategories';
import {POSSIBLE_FEES} from 'lib/enums';
import {paymentMethodLabels} from 'lib/labels';
import React from 'react';
import {
  amountInputProps,
  bigAmountInputProps,
  checkIfRegional,
  checkIfRegionAll,
  InputAddon,
  isFeeWithPaymentMethods
} from 'routes/Account/Billing/utils';

const {Option} = Select;

interface MasterAccountDefaultsProps {
  formProps?: any;
}

const DEFAULT_STEP_FEES = {
  from: 0,
  trxFlatFee: 24,
  trxFee: 0.9,
  bizumTrxFlatFee: 34,
  bizumTrxFee: 1.34
};

export const MasterAccountDefaultsForm: React.FC<MasterAccountDefaultsProps> = ({formProps}) => {
  const {values, setFieldValue} = useFormikContext<UpdateMasterAccountInput>();
  const categories = useBusinessCategories();

  // Utility function to determine input props based on fee type and region
  const getInputNumberProps = (extraFee: AccountExtraFeeInput | undefined, index: number) => {
    if (extraFee?.type && checkIfRegional(extraFee.type)) {
      return amountInputProps;
    }

    if (extraFee?.region && checkIfRegionAll(extraFee.region)) {
      return {
        ...(extraFee?.type === AccountExtraFeeType.PERCENT ? undefined : amountInputProps),
        addonAfter: (
          <Select name={`accountDefaults.fees.extra.${index}.type`} style={{width: 60}}>
            <Select.Option value="percent">%</Select.Option>
            <Select.Option value="money">€</Select.Option>
          </Select>
        )
      };
    } else {
      return {
        addonAfter: <InputAddon>%</InputAddon>
      };
    }
  };

  return (
    <>
      {/** Billing plan */}
      <FormItem name="accountDefaults.billingPlan" label="Billing plan">
        <Select
          name="accountDefaults.billingPlan"
          options={Object.values(BillingPlans).map((text) => ({
            value: text,
            label: text.replace('_', ' ')
          }))}
        />
      </FormItem>

      {/* Business */}
      <FormItem name="accountDefaults.business.mcc" label="Businesss category">
        <Select
          name="accountDefaults.business.mcc"
          placeholder="Business category"
          optionFilterProp="label"
          showSearch
          options={categories}
        />
      </FormItem>
      <FormItem name="accountDefaults.business.website" label="Website">
        <UrlInput name="accountDefaults.business.website" />
      </FormItem>
      <FormItem name="accountDefaults.business.servicesDescription" label="Services Description">
        <Input.TextArea name="accountDefaults.business.servicesDescription" />
      </FormItem>

      {/* Language */}
      <FormItem name="accountDefaults.language" label="Language">
        <Select name="accountDefaults.language">
          <Option value="en">English</Option>
          <Option value="es">Spanish</Option>
          <Option value="ca">Catalan</Option>
        </Select>
      </FormItem>

      {/* Fees */}
      <FormItem name="accountDefaults.fees.trxFee" label="Card transaction fee">
        <InputNumber name="accountDefaults.fees.trxFee" {...amountInputProps} />
      </FormItem>
      <FormItem name="accountDefaults.fees.trxFlatFee" label="Flat fee per card transaction">
        <InputNumber name="accountDefaults.fees.trxFlatFee" {...amountInputProps} />
      </FormItem>
      <FormItem name="accountDefaults.fees.dailyPlatformFee" label="Daily platform usage fee">
        <InputNumber name="accountDefaults.fees.dailyPlatformFee" {...amountInputProps} />
      </FormItem>
      <FormItem name="accountDefaults.fees.dailyExtraSeatsFee" label="Daily extra seat fee in EUR">
        <InputNumber name="accountDefaults.fees.dailyExtraSeatsFee" {...amountInputProps} />
      </FormItem>
      <FormItem name="accountDefaults.fees.highRiskFee" label="High risk fee per transaction">
        <InputNumber
          name="accountDefaults.fees.highRiskFee"
          addonAfter={<InputAddon>%</InputAddon>}
        />
      </FormItem>
      <FieldArray name="accountDefaults.fees.extra">
        {({remove, push}) => (
          <>
            {values.accountDefaults?.fees?.extra?.map((extraFee: AccountExtraFeeInput, index) => (
              <FormItem
                name={`accountDefaults.fees.extra.${index}.name`}
                key={'accountDefaults.fees.extra.' + index}
                wrapperCol={{span: 24}}>
                <Row gutter={2}>
                  <Col span={formProps.labelCol?.span}>
                    <AutoComplete
                      name={`accountDefaults.fees.extra.${index}.name`}
                      filterOption
                      placeholder="Fee name"
                      options={POSSIBLE_FEES.map((text) => ({
                        value: text,
                        label: text
                      }))}
                    />
                  </Col>
                  <Col>
                    <InputNumber
                      name={`accountDefaults.fees.extra.${index}.amount`}
                      placeholder="Amount"
                      style={{width: 120}}
                      {...getInputNumberProps(extraFee, index)}
                    />
                  </Col>
                  <Col span={7}>
                    <Select
                      name={`accountDefaults.fees.extra.${index}.${
                        extraFee?.type && checkIfRegional(extraFee.type) ? 'type' : 'region'
                      }`}
                      onChange={(value) => {
                        if (checkIfRegional(value)) {
                          setFieldValue(`accountDefaults.fees.extra.${index}.type`, value);
                        } else {
                          setFieldValue(
                            `accountDefaults.fees.extra.${index}.type`,
                            AccountExtraFeeType.PERCENT
                          );
                        }
                      }}>
                      <Select.Option value={AccountExtraFeeType.DAILY}>per day</Select.Option>
                      <Select.Option value={AccountExtraFeeType.MONTHLY}>per month</Select.Option>
                      <Select.Option value={AccountExtraFeeType.ONE_TIME}>one time</Select.Option>
                      <Select.Option value={AccountExtraFeeRegion.DOMESTIC}>
                        of successful EU credit cards transactions
                      </Select.Option>
                      <Select.Option value={AccountExtraFeeRegion.NON_DOMESTIC}>
                        of successful <b>non</b> EU credit cards transactions
                      </Select.Option>
                      <Select.Option value={AccountExtraFeeRegion.ALL}>
                        of successful transactions
                      </Select.Option>
                    </Select>
                  </Col>
                  {isFeeWithPaymentMethods(extraFee) && (
                    <Col>
                      <Select
                        mode="multiple"
                        placeholder="Payment methods"
                        name={`accountDefaults.fees.extra.${index}.paymentMethods`}
                        style={{width: 170}}>
                        {Object.values(PaymentMethods).map((paymentMethod) => (
                          <Select.Option key={paymentMethod} value={paymentMethod}>
                            {paymentMethodLabels[paymentMethod]}
                          </Select.Option>
                        ))}
                      </Select>
                    </Col>
                  )}
                  <Col>
                    <Select
                      name={`accountDefaults.fees.extra.${index}.masterAccountFee`}
                      style={{width: 170}}>
                      <Select.Option value={true}>Master account fee</Select.Option>
                      <Select.Option value={false}>MONEI fee</Select.Option>
                    </Select>
                  </Col>
                  <Col span={2}>
                    <Button icon onClick={() => remove(index)}>
                      <CloseOutlined />
                    </Button>
                  </Col>
                </Row>
              </FormItem>
            ))}
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="add">
              <Button
                onClick={() =>
                  push({
                    name: '',
                    amount: 0,
                    type: AccountExtraFeeType.PERCENT,
                    region: AccountExtraFeeRegion.ALL
                  })
                }
                type="dashed"
                icon={<PlusOutlined />}>
                Add extra fee
              </Button>
            </FormItem>
          </>
        )}
      </FieldArray>
      <FieldArray name="accountDefaults.fees.steps">
        {({remove, push}) => (
          <>
            {values.accountDefaults.fees.steps?.map((step, index) => (
              <Card
                type="inner"
                size="small"
                key={'accountDefaults.fees.steps.' + index}
                style={{marginBottom: 10}}
                title={`Step #${index + 1}`}
                extra={
                  <AnchorLink onClick={() => remove(index)}>
                    <DeleteOutlined /> Remove step
                  </AnchorLink>
                }>
                <FormItem
                  name={`accountDefaults.fees.steps.${index}.from`}
                  label="From monthly volume in EUR"
                  style={{marginRight: 10}}>
                  <InputNumber
                    name={`accountDefaults.fees.steps.${index}.from`}
                    style={{width: 200}}
                    step={100000}
                    {...bigAmountInputProps}
                  />
                </FormItem>
                <FormItem
                  name={`accountDefaults.fees.steps.${index}.trxFee`}
                  label="Card transaction fee">
                  <InputNumber name={`accountDefaults.fees.steps.${index}.trxFee`} addonAfter="%" />
                </FormItem>
                <FormItem
                  name={`accountDefaults.fees.steps.${index}.bizumTrxFee`}
                  label="Bizum transaction fee">
                  <InputNumber
                    name={`accountDefaults.fees.steps.${index}.bizumTrxFee`}
                    addonAfter="%"
                  />
                </FormItem>
                <FormItem
                  name={`accountDefaults.fees.steps.${index}.trxFlatFee`}
                  label="Flat fee per card transaction">
                  <InputNumber
                    name={`accountDefaults.fees.steps.${index}.trxFlatFee`}
                    {...amountInputProps}
                  />
                </FormItem>
                <FormItem
                  name={`accountDefaults.fees.steps.${index}.bizumTrxFlatFee`}
                  label="Flat fee per bizum transaction">
                  <InputNumber
                    name={`accountDefaults.fees.steps.${index}.bizumTrxFlatFee`}
                    {...amountInputProps}
                  />
                </FormItem>
              </Card>
            ))}
            <FormItem
              wrapperCol={{
                offset: formProps.labelCol?.span,
                ...formProps.wrapperCol
              }}
              name="add">
              <Button
                style={{marginTop: 20}}
                onClick={() => push(DEFAULT_STEP_FEES)}
                type="dashed"
                icon={<PlusOutlined />}>
                Add new billing step
              </Button>
            </FormItem>
          </>
        )}
      </FieldArray>
    </>
  );
};
